/**
 * @file
 * @fileOverview This file contains the MarkdownDisplay class for rendering markdown text in a Phaser scene.
 * @version 1.0.0
 * @date 2025-03-22
 */

import "phaser";
import showdown from 'showdown';

/**
 * MarkdownDisplay class extending Phaser.GameObjects.Container
 * @class
 * @extends Phaser.GameObjects.Container
 * @classdesc A component that renders markdown formatted text on a virtual screen.
 */
class MarkdownDisplay extends Phaser.GameObjects.Container {
  /**
   * Creates a new MarkdownDisplay instance.
   * @constructor
   * @param {Phaser.Scene} scene - The Scene to which this display belongs.
   * @param {string} id - Unique identifier for the display.
   * @param {number} x - The x position of the display.
   * @param {number} y - The y position of the display.
   * @param {number} width - Width of the display area.
   * @param {number} height - Height of the display area.
   * @param {string} [text=''] - Initial markdown text to display.
   * @param {Object} [options={}] - Additional options for customizing the display.
   * @param {string} [options.backgroundColor='#333333'] - Background color of the display.
   * @param {string} [options.textColor='#ffffff'] - Default color of the text.
   * @param {string} [options.fontSize='16px'] - Font size of the text.
   * @param {string} [options.fontFamily='Arial'] - Font family of the text.
   * @param {number} [options.padding=20] - Padding around the text.
   * @param {boolean} [options.scrollable=true] - Whether the content is scrollable.
   */
  constructor(
    scene,
    id,
    x,
    y,
    width,
    height,
    text = '',
    options = {}
  ) {
    super(scene, x, y);
    this.id = id;
    
    // Default options
    this.options = {
      backgroundColor: options.backgroundColor || '#333333',
      textColor: options.textColor || '#ffffff',
      fontSize: options.fontSize || '16px',
      fontFamily: options.fontFamily || 'Arial',
      padding: options.padding !== undefined ? options.padding : 20,
      scrollable: options.scrollable !== undefined ? options.scrollable : true,
      lineSpacing: options.lineSpacing || 6,
      headingScale: options.headingScale || {
        h1: 2.0,
        h2: 1.5,
        h3: 1.25,
        h4: 1.1,
        h5: 1.0,
        h6: 0.9
      },
      showdownOptions: options.showdownOptions || {
        simpleLineBreaks: true,
        strikethrough: true,
        tables: true,
        tasklists: true,
        emoji: true
      },
      ...options
    };
    
    // Initialize Showdown converter
    this.converter = new showdown.Converter(this.options.showdownOptions);

    // Create background
    this.background = scene.add.rectangle(
      0,
      0,
      width,
      height,
      Phaser.Display.Color.HexStringToColor(this.options.backgroundColor).color
    );
    this.background.setOrigin(0);
    this.background.setPosition(-width/2, -height/2);
    
    // Dimensions
    this.width = width;
    this.height = height;
    
    // Content container for scrolling
    this.contentContainer = scene.add.container(0, 0);
    
    // Text objects array to manage multiple text elements
    this.textObjects = [];
    
    // Current raw markdown text
    this.markdownText = text;
    
    // Scroll position
    this.scrollY = 0;
    this.maxScrollY = 0;
    
    // Add components to container
    this.add(this.background);
    this.add(this.contentContainer);
    
    // Set container size
    this.setSize(width, height);
    
    // Create mask for scrolling content
    if (this.options.scrollable) {
      const mask = scene.make.graphics({});
      mask.fillRect(-width/2, -height/2, width, height);
      this.contentContainer.setMask(mask.createGeometryMask());
      
      // Setup scrolling interaction
      this.background.setInteractive();
      this.background.on('wheel', this.handleScroll, this);
      
      // Add drag functionality
      this.background.on('pointerdown', this.startDrag, this);
      this.background.on('pointermove', this.doDrag, this);
      this.background.on('pointerup', this.endDrag, this);
      this.background.on('pointerout', this.endDrag, this);
    }
    
    // Render initial text
    if (text) {
      this.setText(text);
    }
    
    // Add to scene
    scene.add.existing(this);
  }

  /**
   * Get the unique identifier of the display.
   * @returns {string} The display's ID.
   */
  getId() {
    return this.id;
  }

  /**
   * Set new markdown text content.
   * @param {string} text - The new markdown text to display.
   */
  setText(text) {
    this.markdownText = text;
    this.renderMarkdown();
  }

  /**
   * Get the current markdown text.
   * @returns {string} The current markdown text.
   */
  getText() {
    return this.markdownText;
  }

  /**
   * Clear all text from the display.
   */
  clearText() {
    this.markdownText = '';
    this.renderMarkdown();
  }

  /**
   * Parse and render markdown text into Phaser text objects using Showdown.
   * @private
   */
  renderMarkdown() {
    // Clear previous text objects
    this.textObjects.forEach(obj => obj.destroy());
    this.textObjects = [];
    this.contentContainer.removeAll();
    
    if (!this.markdownText) return;
    
    // Convert markdown to HTML using Showdown
    const htmlContent = this.converter.makeHtml(this.markdownText);
    
    // Create a temporary DOM element to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    let yPos = -this.height/2 + this.options.padding;
    const baseLineHeight = parseInt(this.options.fontSize, 10);
    let lineHeight = baseLineHeight;
    
    // Helper function to create text objects for formatted content
    const createTextObject = (text, styles, x, y, width) => {
      const textStyle = {
        fontSize: this.options.fontSize,
        fontFamily: this.options.fontFamily,
        color: this.options.textColor,
        wordWrap: { width: width },
        ...styles
      };
      
      const textObj = this.scene.add.text(x, y, text, textStyle);
      this.textObjects.push(textObj);
      this.contentContainer.add(textObj);
      
      return textObj;
    };
    
    // Process DOM elements to create Phaser text objects
    const processNode = (node, indent = 0) => {
      const availableWidth = this.width - (this.options.padding * 2) - (indent * 20);
      const startX = -this.width/2 + this.options.padding + (indent * 20);
      
      switch (node.nodeName) {
        case 'H1':
        case 'H2':
        case 'H3':
        case 'H4':
        case 'H5':
        case 'H6':
          const level = parseInt(node.nodeName.charAt(1));
          const headingScale = this.options.headingScale[`h${level}`];
          const headingSize = baseLineHeight * headingScale;
          
          const headingObj = createTextObject(
            node.textContent, 
            { 
              fontSize: `${headingSize}px`,
              fontStyle: 'bold' 
            }, 
            startX, 
            yPos, 
            availableWidth
          );
          
          yPos += headingObj.height + this.options.lineSpacing * 2;
          break;
          
        case 'P':
          // Process paragraph content with potential inline formatting
          this.processInlineFormatting(node, startX, yPos, availableWidth);
          yPos += lineHeight + this.options.lineSpacing * 2;
          break;
          
        case 'UL':
        case 'OL':
          // Process list items
          Array.from(node.children).forEach((item, index) => {
            if (item.nodeName === 'LI') {
              const bullet = node.nodeName === 'UL' ? '• ' : `${index + 1}. `;
              
              const bulletObj = createTextObject(
                bullet,
                {},
                startX,
                yPos,
                20
              );
              
              this.processInlineFormatting(
                item, 
                startX + bulletObj.width, 
                yPos, 
                availableWidth - bulletObj.width
              );
              
              yPos += lineHeight + this.options.lineSpacing;
            }
          });
          yPos += this.options.lineSpacing;
          break;
          
        case 'PRE':
          // Process code blocks
          const codeNode = node.querySelector('code');
          const codeText = codeNode ? codeNode.textContent : node.textContent;
          
          const codeBackgroundWidth = availableWidth;
          const codeBackground = this.scene.add.rectangle(
            startX + codeBackgroundWidth/2,
            yPos + baseLineHeight/2,
            codeBackgroundWidth,
            baseLineHeight + 14,
            0x222222
          );
          
          this.textObjects.push(codeBackground);
          this.contentContainer.add(codeBackground);
          
          const codeObj = createTextObject(
            codeText,
            {
              fontFamily: 'monospace',
              color: '#BBBBBB',
              fontSize: `${baseLineHeight}px`
            },
            startX + 5,
            yPos + 4,
            availableWidth - 10
          );
          
          // Resize the background to fit the text
          codeBackground.height = codeObj.height + 8;
          codeBackground.y = yPos + codeObj.height/2 + 4;
          
          yPos += codeObj.height + this.options.lineSpacing + 8;
          break;
          
        case 'HR':
          const hrLine = this.scene.add.graphics();
          hrLine.lineStyle(2, Phaser.Display.Color.HexStringToColor(this.options.textColor).color, 0.5);
          hrLine.lineBetween(
            -this.width/2 + this.options.padding,
            yPos + baseLineHeight/2,
            this.width/2 - this.options.padding,
            yPos + baseLineHeight/2
          );
          
          this.textObjects.push(hrLine);
          this.contentContainer.add(hrLine);
          
          yPos += baseLineHeight + this.options.lineSpacing;
          break;
          
        case 'BLOCKQUOTE':
          // Add a vertical line for the blockquote
          const quoteBar = this.scene.add.graphics();
          quoteBar.lineStyle(4, 0x777777, 1);
          quoteBar.lineBetween(
            startX,
            yPos,
            startX,
            yPos + 20 // Initial height, will be updated
          );
          
          this.textObjects.push(quoteBar);
          this.contentContainer.add(quoteBar);
          
          const startQuoteY = yPos;
          
          // Process blockquote content with increased indent
          Array.from(node.childNodes).forEach(child => {
            if (child.nodeType === 1) { // Element node
              processNode(child, indent + 1);
            } else if (child.nodeType === 3 && child.textContent.trim()) { // Text node
              this.processTextNode(
                child.textContent,
                startX + 10, // Indent text inside blockquote
                yPos,
                availableWidth - 10,
                {}
              );
              yPos += lineHeight + this.options.lineSpacing;
            }
          });
          
          // Update the height of the quote bar
          quoteBar.clear();
          quoteBar.lineStyle(4, 0x777777, 1);
          quoteBar.lineBetween(
            startX,
            startQuoteY,
            startX,
            yPos
          );
          
          yPos += this.options.lineSpacing;
          break;
          
        case 'TABLE':
          // Basic table implementation
          const rows = Array.from(node.querySelectorAll('tr'));
          const headerRow = node.querySelector('thead tr');
          
          if (rows.length > 0) {
            const colCount = rows[0].querySelectorAll('td, th').length;
            const colWidth = availableWidth / colCount;
            
            rows.forEach((row, rowIndex) => {
              const cells = Array.from(row.querySelectorAll('td, th'));
              const isHeader = row === headerRow || rowIndex === 0 && !headerRow;
              
              let rowHeight = 0;
              
              // Process each cell in the row
              cells.forEach((cell, colIndex) => {
                const cellX = startX + (colIndex * colWidth);
                
                const cellStyles = {
                  fontSize: this.options.fontSize,
                  fontFamily: this.options.fontFamily,
                  wordWrap: { width: colWidth - 10 },
                  align: 'center'
                };
                
                if (isHeader) {
                  cellStyles.fontStyle = 'bold';
                }
                
                const cellObj = createTextObject(
                  cell.textContent,
                  cellStyles,
                  cellX + 5,
                  yPos + 4,
                  colWidth - 10
                );
                
                rowHeight = Math.max(rowHeight, cellObj.height + 8);
                
                // Add cell border
                const border = this.scene.add.graphics();
                border.lineStyle(1, 0x777777, 1);
                border.strokeRect(
                  cellX,
                  yPos,
                  colWidth,
                  rowHeight
                );
                
                this.textObjects.push(border);
                this.contentContainer.add(border);
              });
              
              yPos += rowHeight;
            });
            
            yPos += this.options.lineSpacing;
          }
          break;
          
        default:
          // Process child nodes for elements we don't handle specifically
          if (node.childNodes.length) {
            Array.from(node.childNodes).forEach(child => {
              if (child.nodeType === 1) { // Element node
                processNode(child, indent);
              } else if (child.nodeType === 3 && child.textContent.trim()) { // Text node
                this.processTextNode(
                  child.textContent,
                  startX,
                  yPos,
                  availableWidth,
                  {}
                );
                yPos += lineHeight + this.options.lineSpacing;
              }
            });
          }
          break;
      }
    };
    
    // Process each top-level element
    Array.from(tempDiv.childNodes).forEach(node => {
      if (node.nodeType === 1) { // Element node
        processNode(node);
      }
    });
    
    // Calculate total content height for scrolling
    this.contentHeight = yPos + this.options.padding - (-this.height/2);
    this.maxScrollY = Math.max(0, this.contentHeight - this.height);
    
    // Reset scroll position
    this.scrollY = 0;
    this.updateScroll();
  }
  
  /**
   * Process inline formatting within text nodes
   * @param {Node} parentNode - Node containing text with potential inline formatting
   * @param {number} startX - Starting X position
   * @param {number} startY - Starting Y position
   * @param {number} maxWidth - Maximum width for text wrapping
   * @private 
   */
  processInlineFormatting(parentNode, startX, startY, maxWidth) {
    let xPos = startX;
    let yPos = startY;
    let lineWidth = 0;
    let lineHeight = parseInt(this.options.fontSize, 10);
    
    // Process each child node
    Array.from(parentNode.childNodes).forEach(node => {
      if (node.nodeType === 3) { // Text node
        // Process text content
        const textObj = this.scene.add.text(
          xPos,
          yPos,
          node.textContent,
          {
            fontSize: this.options.fontSize,
            fontFamily: this.options.fontFamily,
            color: this.options.textColor,
            wordWrap: { width: maxWidth - lineWidth }
          }
        );
        
        xPos += textObj.width;
        lineWidth += textObj.width;
        
        this.textObjects.push(textObj);
        this.contentContainer.add(textObj);
      } else if (node.nodeType === 1) { // Element node
        // Apply appropriate styling based on tag
        const style = {
          fontSize: this.options.fontSize,
          fontFamily: this.options.fontFamily,
          color: this.options.textColor,
          wordWrap: { width: maxWidth - lineWidth }
        };
        
        switch (node.nodeName) {
          case 'STRONG':
          case 'B':
            style.fontStyle = 'bold';
            break;
          case 'EM':
          case 'I':
            style.fontStyle = 'italic';
            break;
          case 'CODE':
            style.fontFamily = 'monospace';
            style.backgroundColor = '#222222';
            style.color = '#BBBBBB';
            break;
          case 'A':
            style.color = '#6699CC';
            style.fontStyle = 'bold';
            break;
        }
        
        // Create text object with appropriate styling
        const textObj = this.scene.add.text(
          xPos,
          yPos,
          node.textContent,
          style
        );
        
        xPos += textObj.width;
        lineWidth += textObj.width;
        
        this.textObjects.push(textObj);
        this.contentContainer.add(textObj);
      }
    });
  }
  
  /**
   * Process a text node and create a text object
   * @param {string} text - Text content
   * @param {number} x - X position
   * @param {number} y - Y position 
   * @param {number} width - Maximum width
   * @param {Object} styles - Additional text styles
   * @private
   */
  processTextNode(text, x, y, width, styles) {
    const textObj = this.scene.add.text(
      x,
      y,
      text,
      {
        fontSize: this.options.fontSize,
        fontFamily: this.options.fontFamily,
        color: this.options.textColor,
        wordWrap: { width: width },
        ...styles
      }
    );
    
    this.textObjects.push(textObj);
    this.contentContainer.add(textObj);
    
    return textObj;
  }

  /**
   * Handle mouse wheel scrolling.
   * @param {Phaser.Input.Pointer} pointer - The pointer that triggered the event.
   * @param {number} deltaX - Horizontal scroll amount.
   * @param {number} deltaY - Vertical scroll amount.
   * @private
   */
  handleScroll(pointer, deltaX, deltaY) {
    if (!this.options.scrollable) return;
    
    // Update scroll position
    this.scrollY = Phaser.Math.Clamp(
      this.scrollY + deltaY * 0.5,
      0,
      this.maxScrollY
    );
    
    this.updateScroll();
  }

  /**
   * Start drag scrolling operation.
   * @param {Phaser.Input.Pointer} pointer - The pointer that triggered the event.
   * @private
   */
  startDrag(pointer) {
    if (!this.options.scrollable) return;
    
    this.isDragging = true;
    this.lastPointerY = pointer.y;
  }

  /**
   * Perform drag scrolling.
   * @param {Phaser.Input.Pointer} pointer - The pointer that triggered the event.
   * @private
   */
  doDrag(pointer) {
    if (!this.options.scrollable || !this.isDragging) return;
    
    const deltaY = this.lastPointerY - pointer.y;
    this.lastPointerY = pointer.y;
    
    // Update scroll position
    this.scrollY = Phaser.Math.Clamp(
      this.scrollY + deltaY,
      0,
      this.maxScrollY
    );
    
    this.updateScroll();
  }

  /**
   * End drag scrolling operation.
   * @private
   */
  endDrag() {
    this.isDragging = false;
  }

  /**
   * Update the position of content based on scroll position.
   * @private
   */
  updateScroll() {
    if (!this.options.scrollable) return;
    
    this.contentContainer.y = -this.scrollY;
  }

  /**
   * Set the size of the display area.
   * @param {number} width - The new width.
   * @param {number} height - The new height.
   */
  setDisplaySize(width, height) {
    this.width = width;
    this.height = height;
    
    // Update background
    this.background.setSize(width, height);
    this.background.setPosition(-width/2, -height/2);
    
    // Update mask if scrollable
    if (this.options.scrollable) {
      const mask = this.scene.make.graphics({});
      mask.fillRect(-width/2, -height/2, width, height);
      this.contentContainer.setMask(mask.createGeometryMask());
    }
    
    // Re-render content to adjust for new width
    this.renderMarkdown();
  }

  /**
   * Set theme colors for the display.
   * @param {string} backgroundColor - New background color.
   * @param {string} textColor - New text color.
   */
  setTheme(backgroundColor, textColor) {
    this.options.backgroundColor = backgroundColor;
    this.options.textColor = textColor;
    
    // Update background
    this.background.setFillStyle(
      Phaser.Display.Color.HexStringToColor(backgroundColor).color
    );
    
    // Re-render with new colors
    this.renderMarkdown();
  }

  /**
   * Set the font size for text display.
   * @param {string} fontSize - New font size (e.g., '18px').
   */
  setFontSize(fontSize) {
    this.options.fontSize = fontSize;
    this.renderMarkdown();
  }

  /**
   * Set the font family for text display.
   * @param {string} fontFamily - New font family.
   */
  setFontFamily(fontFamily) {
    this.options.fontFamily = fontFamily;
    this.renderMarkdown();
  }
}

export default MarkdownDisplay;